import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { submitToGoogleSheets } from '../services/googleSheets';
import { Link } from 'react-router-dom';

const AVAILABLE_POSITIONS = [
  { 
    id: 1, 
    title: 'Full Stack Software Engineer',
    levels: ['Intern', 'Junior', 'Mid', 'Senior'],
    requiresPortfolio: true,
    specificQuestions: {
      techExpertise: 'Are you an expert with full stack development (React, Node.js)?',
      aiExperience: 'Do you have experience creating AI features?'
    }
  },
  { 
    id: 2, 
    title: 'Backend Software Engineer',
    levels: ['Intern', 'Junior', 'Mid', 'Senior'],
    requiresPortfolio: true,
    specificQuestions: {
      techExpertise: 'Are you an expert with node js and python?',
      aiExperience: 'Do you have experience creating AI features?'
    }
  },
  { 
    id: 3, 
    title: 'Frontend Software Engineer',
    levels: ['Intern', 'Mid', 'Senior'],
    requiresPortfolio: true,
    specificQuestions: {
      techExpertise: 'Are you an expert with react, tailwind and typescript?',
      aiExperience: 'Do you have experience creating AI features?'
    }
  },
  { 
    id: 4, 
    title: 'UX/UI Designer',
    levels: ['Junior', 'Mid', 'Senior'],
    requiresPortfolio: true,
    specificQuestions: {
      techExpertise: 'What design tools are you proficient with? (Figma, Adobe XD, etc.)',
      aiExperience: 'Do you have experience with AI-powered design tools?'
    }
  }
];

function Careers() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    position: '',
    level: '',
    expectedSalary: '',
    techExpertise: '',
    aiExperience: '',
    portfolio: '',
    englishFluency: '',
    noticePeriod: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate salary
    if (!formData.expectedSalary || formData.expectedSalary.toLowerCase().includes('na')) {
      toast.error('Please provide a valid expected salary with currency');
      return;
    }

    // Validate portfolio
    if (!formData.portfolio) {
      toast.error('Portfolio link is required');
      return;
    }

    try {
      // Show loading state
      const loadingToast = toast.loading('Submitting application...');
      
      await submitToGoogleSheets(formData);
      
      // Clear form
      setFormData({
        name: '',
        email: '',
        position: '',
        level: '',
        expectedSalary: '',
        techExpertise: '',
        aiExperience: '',
        portfolio: '',
        englishFluency: '',
        noticePeriod: '',
      });

      // Show success message
      toast.dismiss(loadingToast);
      toast.success('Application submitted successfully!');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit application. Please try again.');
    }
  };

  const selectedPosition = AVAILABLE_POSITIONS.find(pos => pos.title === formData.position);

  const positions = [
    {
      title: 'Full Stack Software Engineer',
      department: 'Engineering',
      location: 'Worldwide (Remote)',
      salaryRange: '$1,000-$2,000 USD',
      requirements: [
        'Minimum 5 years of fullstack development experience with a proven track record in managing and leading development teams.',
        'Strong proficiency in frontend technologies including JavaScript, React, Tailwind CSS, and TypeScript.',
        'Expertise in backend development using Node.js, with hands-on experience in building and managing microservices architectures.',
        'Extensive experience working in agile environments, with a deep understanding of agile methodologies and best practices.',
        'Proficiency with RESTful APIs, database design (SQL and/or NoSQL), and CI/CD pipelines.',
        'Excellent problem-solving skills, strong communication abilities, and a passion for fostering a collaborative environment.',
        'Bachelor’s degree in Computer Science, Engineering, or a related field, or equivalent practical experience.',
      ]
    },
    {
      title: 'Backend Software Engineer',
      department: 'Engineering',
      location: 'Worldwide (Remote)',
      salaryRange: '$700-$1,200 USD',
      requirements: [
        'Minimum 3 years of hands-on experience in backend development, particularly using Node.js.',
        'Strong understanding and experience with microservice architectures.',
        'Experience integrating APIs from e-commerce platforms, payment systems, logistics providers, and other third-party services.',
        'Familiarity with designing secure and scalable backend systems.',
        'Proficiency with databases (SQL and/or NoSQL) and RESTful API design.',
        'Experience with agile development practices and CI/CD pipelines.',
        'Excellent problem-solving skills and attention to detail.',
        'Bachelor’s degree in Computer Science, Engineering, or a related field, or equivalent practical experience.',
      ]
    },
    {
      title: 'Frontend Software Engineer',
      department: 'Engineering',
      location: 'Worldwide (Remote)',
      salaryRange: '$700-$1,200 USD',
      requirements: [
        'Minimum 3 years of hands-on experience in frontend development.',
        'Proficiency in JavaScript, React, Tailwind CSS, and TypeScript.',
        'Strong understanding of responsive design principles and best practices.',
        'Experience with RESTful APIs and integrating frontend applications with backend services.',
        'Familiarity with agile development practices and version control systems (e.g., Git).',
        'Excellent problem-solving skills and attention to detail.'
      ]
    },
    {
      title: 'UX/UI Designer',
      department: 'Design',
      location: 'Worldwide (Remote)',
      salaryRange: '$700-$1,200 USD',
      requirements: [
        'Minimum 3 years of experience in UX/UI design, with a focus on e-commerce application interfaces.',
        'Proficiency in Figma for rapid screen building and prototyping.',
        'Strong portfolio showcasing user-centric design solutions for web or mobile applications.',
        'Excellent understanding of design principles, responsive design, and interaction design.',
        'Ability to work efficiently in a fast-paced, remote environment.',
        'Strong communication and collaboration skills.',
        "Bachelor's degree in Design, Human-Computer Interaction, or a related field, or equivalent practical experience."
      ]
    },
    {
      title: 'Product Owner',
      department: 'Product',
      location: 'Worldwide (Remote)',
      salaryRange: '$700-$1,200 USD',
      requirements: [
        "3–5 years' experience in product management, preferably in e-commerce, SaaS, or international business platforms",
        'Proven track record in launching and managing digital products from concept through release',
        'Strong understanding of Agile methodologies (e.g., Scrum or Kanban)',
        'Excellent communication and stakeholder management skills',
        'Ability to translate business needs into technical requirements and actionable user stories',
        'Analytical mindset with a strong foundation in data-driven decision-making',
        'Familiarity with cross-border e-commerce challenges and opportunities (e.g., localization, multi-currency, international logistics)',
        'Proficient in digital product development tools and project management software',
        "Bachelor's degree in Business, Computer Science, Engineering, or a related field (advanced degrees or relevant certifications are a plus)"
      ]
    },
    {
      title: 'Technical Project Manager',
      department: 'Engineering',
      location: 'Worldwide (Remote)',
      salaryRange: '$700-$1,200 USD',
      requirements: [
        '3–5 years of experience managing technical projects, preferably in e-commerce, SaaS, or a related technology field',
        'Proven track record of successfully delivering projects on time and within scope',
        'Strong understanding of Agile methodologies (e.g., Scrum or Kanban) and related project management tools',
        'Excellent communication and stakeholder management skills',
        'Ability to navigate complex technical discussions and translate requirements between technical and non-technical stakeholders',
        'Experience with risk management, resource planning, and budget tracking',
        "Bachelor's degree in Computer Science, Engineering, Business, or a related field (advanced degrees or relevant certifications are a plus)"
      ],
    }
  ];

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:py-24 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-base font-semibold text-[#0049ac] tracking-wide uppercase">Join Our Team</h2>
          <p className="mt-1 text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
            Build the Future of MENAP E-commerce
          </p>
          <p className="max-w-xl mt-5 mx-auto text-xl text-gray-500">
            Join SellAbroad and help transform how businesses expand across the Middle East, North Africa, and Pakistan.
          </p>
        </div>

        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-8">Open Positions</h3>
          <div className="space-y-6">
            {positions.map((position, index) => (
              <div 
                key={index} 
                className="bg-gray-100 border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900">{position.title}</h4>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <p className="text-gray-500">{position.department} • {position.location}</p>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {position.salaryRange}
                      </span>
                    </div>
                  </div>
                  <div className="mt-4 md:mt-0">
                    <Link
                      to={`/careers/${position.title.toLowerCase()
                        .replace(/\s+/g, '-')
                        .replace(/\//g, '-')
                        .replace(/full stack/g, 'fullstack')
                        .replace(/[^a-z0-9-]/g, '')}`}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-[#0049ac] rounded-lg hover:bg-[#0049ac]/90 hover:scale-105 transform transition-all duration-300"
                    >
                      View Position
                    </Link>
                  </div>
                </div>
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">Requirements:</h5>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    {position.requirements.map((req, idx) => (
                      <li key={idx}>{req}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">Why Join SellAbroad?</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="relative bg-gray-100 p-6 rounded-lg shadow-sm hover:shadow-md transition-all transform hover:scale-105">
            <div className="absolute -top-4 left-6">
              <div className="rounded-lg bg-[#0049ac] p-3 inline-block">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h4 className="mt-8 text-xl font-semibold text-gray-900">Impact</h4>
              <p className="mt-4 text-gray-600 leading-relaxed">Work on meaningful projects that help businesses expand across MENAP markets.</p>
            </div>
          </div>
          <div className="relative bg-gray-100 p-6 rounded-lg shadow-sm hover:shadow-md transition-all transform hover:scale-105">
            <div className="absolute -top-4 left-6">
              <div className="rounded-lg bg-[#0049ac] p-3 inline-block">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h4 className="mt-8 text-xl font-semibold text-gray-900">Growth</h4>
              <p className="mt-4 text-gray-600 leading-relaxed">Fast-paced environment with opportunities to learn and advance your career.</p>
            </div>
          </div>
          <div className="relative bg-gray-100 p-6 rounded-lg shadow-sm hover:shadow-md transition-all transform hover:scale-105">
            <div className="absolute -top-4 left-6">
              <div className="rounded-lg bg-[#0049ac] p-3 inline-block">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h4 className="mt-8 text-xl font-semibold text-gray-900">Benefits</h4>
              <p className="mt-4 text-gray-600 leading-relaxed">Competitive compensation, flexible remote work, and comprehensive benefits.</p>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Careers;